import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlay, FiInfo, FiStar } from "react-icons/fi";
import ImageWithPlaceholder from "./ImageWithPlaceholder";

export default function HeroSlider({ slides = [] }) {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, slides.length]);

  if (!slides.length) return null;

  const slide = slides[current];

  // Support both your API field names and fallbacks
  const title = slide.title || slide.name;
  const poster = slide.image || slide.banner_image_url || slide.cover_image || slide.poster_url;
  const genre = slide.genres || slide.genre;

  return (
    <section className="relative h-[75vh] md:h-[85vh] min-h-[500px] overflow-hidden">
      {/* Background Images (all preloaded, toggled via opacity) */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? "opacity-100" : "opacity-0"
          }`}
        >
          <ImageWithPlaceholder
            src={s.image || s.banner_image_url || s.cover_image || s.poster_url}
            alt={s.title || s.name}
            className="w-full h-full object-cover"
            iconSize={48}
          />
        </div>
      ))}

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-dark-900/95 via-dark-900/60 to-dark-900/20" />
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-dark-900 to-transparent" />

      {/* Content */}
      <div className="absolute bottom-[12%] md:bottom-[15%] left-5 md:left-10 max-w-xl z-10">
        {/* Tag */}
        <span className="inline-block bg-primary px-3 py-1 rounded text-xs font-bold uppercase tracking-wider mb-4">
          {slide.type === "tvshow" || slide.type === "tv" ? "TV Show" : "Movie"}
        </span>

        {/* Meta */}
        <div className="flex items-center gap-3 mb-3 text-sm text-gray-400">
          {slide.rating && (
            <span className="flex items-center gap-1 text-accent font-semibold">
              <FiStar size={13} /> {slide.rating}
            </span>
          )}
          {slide.year && <span>{slide.year}</span>}
          {slide.duration && <span>{slide.duration}</span>}
          {genre && <span>{genre}</span>}
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-3 drop-shadow-lg">
          {title}
        </h1>

        {/* Description */}
        <p className="text-sm md:text-base text-gray-300 leading-relaxed mb-6 line-clamp-3">
          {slide.description || slide.overview}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/player/${slide.type}/${slide.id}`)}
            className="btn-play-gradient"
          >
            <FiPlay size={18} /> PLAY
          </button>
          <button
            onClick={() => navigate(`/details/${slide.type}/${slide.id}`)}
            className="flex items-center gap-2 px-6 py-3.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full font-semibold text-sm transition-all backdrop-blur-sm uppercase tracking-wide"
          >
            <FiInfo size={16} /> Info
          </button>
        </div>
      </div>

      {/* Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2.5 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i === current ? "bg-primary scale-125" : "bg-white/30 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
